import { EventTypeCode, IFootballConfig, IMainEvent, NameFormat, ParticipantType } from '../../../../packages/types';
import { getIconAsBgImage } from '@sportal365/smp-widget-utils/src/icons';
import { formatPlayerName } from '../utils/nameFormatters';

export const alignByTeamPosition = (event: IMainEvent) => (event.teamPosition === ParticipantType.AWAY ? 'left' : 'right');

export const reverseByTeamPosition = (event: IMainEvent) => (event.teamPosition === ParticipantType.AWAY ? 'row' : 'row-reverse');

export const getIconBasedOnEventType = (eventType: string): string | undefined => {
  switch (eventType) {
    case EventTypeCode.RED_CARD:
      return getIconAsBgImage('red');
    case EventTypeCode.YELLOW_CARD_RED:
      return getIconAsBgImage('yellow-red');
    case EventTypeCode.GOAL:
    case EventTypeCode.OWN_GOAL:
    case EventTypeCode.PENALTY_GOAL:
      return getIconAsBgImage('goal');
  }
};

export const groupEventsByType = (mainEvents: IMainEvent[], dataLabels: IFootballConfig['dataLabels'], nameFormat: NameFormat) => {
  const grouped = mainEvents.reduce(
    (acc, event) => {
      // Normalize goal-related event types for grouping purposes
      const normalizedType = [EventTypeCode.GOAL, EventTypeCode.OWN_GOAL, EventTypeCode.PENALTY_GOAL].includes(
        event.typeCode as EventTypeCode,
      )
        ? EventTypeCode.GOAL // Use GOAL as the group key for all goal-related events
        : event.typeCode;

      const key = `${normalizedType}`;
      if (!acc[key]) {
        acc[key] = {
          typeCode: normalizedType as EventTypeCode,
          homePlayers: [],
          awayPlayers: [],
        };
      }

      // Format the player's name based on the NameFormat prop
      const rawName = event.primaryPlayer?.name || '';
      if (!rawName.trim()) {
        // Skip processing this event if the player's name is missing or empty
        return acc;
      }
      const formattedName = formatPlayerName(rawName, nameFormat);

      // Calculate the minute value, including injury time if applicable
      const actualMinute = event.injuryMinute && event.injuryMinute > 0 ? `${event.minute}+${event.injuryMinute}` : `${event.minute}`;

      const playerInfo = {
        name: formattedName,
        minutes: [actualMinute + "'" + returnMainEventSignature(event.typeCode as EventTypeCode, dataLabels)],
      };

      if (event.teamPosition === ParticipantType.HOME) {
        const existingPlayer = acc[key].homePlayers.find((player) => player.name === playerInfo.name);
        if (existingPlayer) {
          existingPlayer.minutes = [...existingPlayer.minutes, ...playerInfo.minutes].sort((a, b) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            return numA - numB;
          });
        } else {
          acc[key].homePlayers.push(playerInfo as { name: string; minutes: string[] });
        }
      } else if (event.teamPosition === ParticipantType.AWAY) {
        const existingPlayer = acc[key].awayPlayers.find((player) => player.name === playerInfo.name);
        if (existingPlayer) {
          existingPlayer.minutes = [...existingPlayer.minutes, ...playerInfo.minutes].sort((a, b) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            return numA - numB;
          });
        } else {
          acc[key].awayPlayers.push(playerInfo as { name: string; minutes: string[] });
        }
      }

      return acc;
    },
    {} as Record<
      string,
      {
        typeCode: EventTypeCode;
        homePlayers: { name: string; minutes: string[] }[];
        awayPlayers: { name: string; minutes: string[] }[];
      }
    >,
  );

  // Return the grouped data
  return Object.values(grouped);
};

export const returnMainEventSignature = (eventType: string, dataLabels: IFootballConfig['dataLabels']): string | undefined => {
  switch (eventType) {
    case EventTypeCode.PENALTY_GOAL:
      return ` (${dataLabels?.penalty_goal ?? 'P'})`;
    case EventTypeCode.OWN_GOAL:
      return ` (${dataLabels?.own_goal ?? 'OG'})`;
    default:
      return '';
  }
};
